import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Button } from 'blaze-ui/Button'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { VIRTUAL_MARKETER_ONBOARDING_PATH } from 'paths'

import VirtualMarketerUpgradeCtaStyles from '../VirtualMarketerUpgradeCta.module.scss'
import Styles from './VirtualMarketerOffboardButton.module.scss'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { generatePath } from 'react-router-dom'

const useCanShowOffboardButton = () => {
  const workspace = useCurrentWorkspace()
  const auth = useAuth()
  const isInternalUser = auth?.email?.endsWith('@blaze.ai')

  return isInternalUser && workspace?.is_virtual_marketer
}

const ConfirmModal = ({ onConfirm }: { onConfirm: () => void }) => {
  const { closeModal } = useModals()

  return (
    <Modal.Root>
      <Modal.Header title="Are you sure?" />
      <Modal.Content>
        <div className={Styles.modalContent}>
          <Text>This action will reset your autopilot workspace. It will delete all of the following:</Text>
          <div>
            <Text>Brand Profiles</Text>
            <Text>Brand Kits</Text>
            <Text>Integrations</Text>
            <Text>Generated Content</Text>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button onPress={closeModal} variant="secondary">
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button onPress={onConfirm} variant="primary">
            Confirm
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

export const VirtualMarketerOffboardButton = () => {
  const workspace = useCurrentWorkspace()
  const { openModal } = useModals()
  const canShowOffboardButton = useCanShowOffboardButton()

  if (!canShowOffboardButton) return null

  const resetVirtualMarketerWorkspace = async () => {
    if (!workspace) return

    const dismiss = showToastMessage('Processing...')
    const res = await sendToApi(`/w/${workspace.id}/virtual_marketers/offboard`, {
      method: 'PUT',
    })
    dismiss()

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      const path = generatePath(VIRTUAL_MARKETER_ONBOARDING_PATH, { workspaceId: workspace.id })
      showToastMessage(
        <span>Autopilot reset successfully</span>,
        'success',
        { variant: 'proceed', type: 'link', to: path, label: 'Go to Onboarding' },
        2.5,
      )
    } else {
      showToastMessage('Failed to reset', 'error')
    }
  }

  const openConfirmModal = () => {
    openModal(ConfirmModal, { onConfirm: resetVirtualMarketerWorkspace }, { newStack: true })
  }

  return <Button onPress={openConfirmModal}>Reset Autopilot</Button>
}

export const VirtualMarketerOffboardButtonContainer = () => {
  const canShowOffboardButton = useCanShowOffboardButton()

  if (!canShowOffboardButton) return null

  return (
    <div className={VirtualMarketerUpgradeCtaStyles.container}>
      <VirtualMarketerOffboardButton />
    </div>
  )
}
